import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { PortalLedgerResponse } from '@propmgt/shared';
import { api, errorMessage } from '../../api/client';
import { describeBalance, formatCents } from '../../lib/money';
import { formatDate } from '../../lib/date';
import { ErrorState, Loading, StatusMessage } from '../../components/ui/Feedback';

const CREDIT_TYPES = new Set(['payment', 'credit']);

export function LedgerView({ data }: { data: PortalLedgerResponse }) {
  const [msg, setMsg] = useState<string | null>(null);
  const balance = describeBalance(data.balanceCents);

  async function getReceipt(entryId: string): Promise<void> {
    if (!data.leaseId) return;
    setMsg(null);
    try {
      const res = await api.ledger.receipt(data.leaseId, entryId);
      window.open(res.url, '_blank', 'noopener');
    } catch (err) {
      setMsg(errorMessage(err));
    }
  }

  return (
    <>
      <h1>Rent ledger</h1>
      <p className="subtitle">Your charges, payments, and running balance.</p>

      {msg && <StatusMessage tone="err">{msg}</StatusMessage>}

      <div className="stat" style={{ maxWidth: 280 }}>
        <p className="stat-label">Current balance</p>
        <p className="stat-value">{balance.amount}</p>
        <p className="muted" style={{ margin: 0 }}>
          {balance.label}
        </p>
        {(data.pendingPaymentCents ?? 0) > 0 && (
          <p className="muted" style={{ margin: '0.25rem 0 0' }}>
            Pending: {formatCents(data.pendingPaymentCents ?? 0)} bank payment processing — it is added here only once it
            clears.
          </p>
        )}
      </div>

      <section aria-labelledby="entries-heading">
        <h2 id="entries-heading">Transactions</h2>
        {data.entries.length === 0 ? (
          <p className="muted">No ledger entries yet.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Ledger transactions, oldest first</caption>
              <thead>
                <tr>
                  <th scope="col">Date</th>
                  <th scope="col">Type</th>
                  <th scope="col">Description</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                  <th scope="col" className="num">
                    Balance
                  </th>
                  <th scope="col">Receipt</th>
                </tr>
              </thead>
              <tbody>
                {data.entries.map((e) => {
                  const credit = CREDIT_TYPES.has(e.type);
                  const sign = credit ? '−' : '+';
                  return (
                    <tr key={e.id}>
                      <td>{formatDate(e.effectiveDate ?? e.createdAt)}</td>
                      <td>{e.type}</td>
                      <td>{e.memo ?? '—'}</td>
                      <td className="num">
                        {sign}
                        {formatCents(e.amountCents)}
                      </td>
                      <td className="num">{formatCents(e.balanceAfterCents)}</td>
                      <td>
                        {e.type === 'payment' ? (
                          <button
                            type="button"
                            className="secondary"
                            onClick={() => getReceipt(e.id)}
                          >
                            Receipt
                          </button>
                        ) : (
                          '—'
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section aria-labelledby="schedule-heading">
        <h2 id="schedule-heading">Installment schedule</h2>
        {data.installments.length === 0 ? (
          <p className="muted">No installment schedule is defined.</p>
        ) : (
          <div className="table-wrap card" style={{ padding: 0 }}>
            <table>
              <caption className="visually-hidden">Upcoming installments</caption>
              <thead>
                <tr>
                  <th scope="col">Installment</th>
                  <th scope="col">Next due</th>
                  <th scope="col" className="num">
                    Amount
                  </th>
                </tr>
              </thead>
              <tbody>
                {data.installments.map((inst, i) => (
                  <tr key={`${inst.dayOfMonth}-${i}`}>
                    <td>{inst.label ?? `Day ${inst.dayOfMonth}`}</td>
                    <td>{formatDate(inst.nextDueDate)}</td>
                    <td className="num">{formatCents(inst.amountCents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}

export function Ledger() {
  const query = useQuery({ queryKey: ['portal', 'ledger'], queryFn: () => api.portal.ledger() });
  if (query.isLoading) return <Loading label="Loading your ledger…" />;
  if (query.isError || !query.data)
    return <ErrorState message={errorMessage(query.error)} onRetry={() => query.refetch()} />;
  return <LedgerView data={query.data} />;
}
